import type { ResolveParams } from '@tanstack/react-router';
import type { IsEmptyObject } from '@interxfi/utilities/types/empty-object';
import type { FileRouteTypes } from '../../routeTree.gen';

interface InterpolationResult {
  interpolatedPath: string;
  isMissingParams: boolean;
}

// This works while we've got simple paths, if we introduce optional segments again, this may need to be modified. @tanstack/router-core recently had a breaking change for this
// helper function; it's possible they'll update their exported utilities soon to include a more robust version of this.
function interpolatePath(path: string, params: Record<string, unknown>): InterpolationResult {
  let isMissingParams = false;

  const interpolatedPath = path.replace(/\$([A-Za-z0-9_]+)|\{\$([^}]+)\}/g, (_, paramName, braceParamName) => {
    const key = paramName ?? braceParamName;

    if (!(key in params)) {
      isMissingParams = true;
      return '';
    }

    const value = params[key];

    if (value === null || value === undefined || value === '') {
      isMissingParams = true;
      return '';
    }

    return encodeURIComponent(String(value));
  });

  return { interpolatedPath, isMissingParams };
}

export type EntityPathContext<TKeys> =
  | {
      kind: 'list';
      keys: Partial<TKeys> | undefined;
    }
  | {
      kind: 'detail';
      keys: Partial<TKeys> | undefined;
    };

export type EntityPathBuilder<TKeys> = (context: EntityPathContext<TKeys>) => string | undefined;

export function getResolvedPath<TPath extends FileRouteTypes['to'], TParams extends ResolveParams<TPath>>(
  path: TPath,
  ...args: IsEmptyObject<TParams> extends true ? [params?: Partial<TParams>] : [params: Partial<TParams>]
): IsEmptyObject<TParams> extends true ? TPath : TParams extends ResolveParams<TPath> ? string : undefined {
  const params = (args[0] ?? {}) as Partial<TParams>;

  const { interpolatedPath, isMissingParams } = interpolatePath(path, params);

  if (isMissingParams) {
    return undefined as any;
  }

  return interpolatedPath as any;
}
